import React, { useState, useEffect, useContext } from "react";
import { useTheme } from "./ThemeContext";
import { Box, Typography, CircularProgress, Button } from "@mui/material";
import { DeviceContext } from "../context/DeviceContext";
import axios from "axios";
import PostMain from "./PostMain";

export default function PostHistoryTable({ userId }) {
  const [visibleCount, setVisibleCount] = useState(5);
  const { theme } = useTheme();
  const { isMobile } = useContext(DeviceContext);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPosts = async () => {
      if (!userId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_SC_API_BASE_URL}/api/posts/user/${userId}`
        );
        setPosts(response.data || []);
        setError(null);
      } catch (err) {
        console.error("Error fetching posts:", err);
        setError("Failed to load posts");
        setPosts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, [userId]);

  // Function to update posts after voting
  const updatePosts = (postId, newUpvotes, newDownvotes) => {
    setPosts((prevPosts) =>
      prevPosts.map((post) =>
        post.postId === postId
          ? { ...post, upvotes: newUpvotes, downvotes: newDownvotes }
          : post
      )
    );
  };

  const handleLoadMore = () => {
    setVisibleCount((prev) => prev + 5);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress sx={{ color: theme.palette.primary.main }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          width: "100%",
          p: 3,
          backgroundColor: theme.palette.background.paper,
          borderRadius: "1rem",
        }}
      >
        <Typography variant="body2" color="error" align="center">
          {error}
        </Typography>
      </Box>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <Box
        sx={{
          width: "100%",
          minHeight: isMobile ? "140px" : "180px",
          px: 2,
          py: 3,
          backgroundColor: theme.palette.background.paper,
          borderRadius: "1rem",
          display: "grid",
          placeItems: "center",
        }}
      >
        <Typography
          variant="body2"
          sx={{ color: theme.palette.text.secondary, width: "100%", textAlign: "center" }}>
          No posts yet.
        </Typography>
      </Box>
    );
  }

  const visiblePosts = posts.slice(0, visibleCount);
  const hasMore = visibleCount < posts.length;

  return (
    <Box
      sx={{
        maxHeight: isMobile ? "auto" : "600px",
        overflowY: isMobile ? "visible" : "auto",
        "&::-webkit-scrollbar": {
          width: "8px",
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: theme.palette.background.default,
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: theme.palette.background.gray,
          borderRadius: "4px",
        },
      }}
    >
      {visiblePosts.map((post) => (
        <Box key={post.postId} sx={{ mb: 2 }}>
          <PostMain post={post} updatePosts={updatePosts} />
        </Box>
      ))}
      {hasMore && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <Button
            variant="outlined"
            onClick={handleLoadMore}
            sx={{
              color: theme.palette.primary.main,
              borderColor: theme.palette.primary.main,
              textTransform: "none",
              "&:hover": {
                borderColor: theme.palette.primary.main,
                backgroundColor: "rgba(98, 244, 129, 0.1)",
              },
            }}
          >
            Load More ({posts.length - visibleCount} remaining)
          </Button>
        </Box>
      )}
    </Box>
  );
}
